import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import UserService from '../services/user.service';
import { Spinner } from 'react-bootstrap';

const BoardInvoiceSettings = () => {
    const { user: currentUser } = useSelector((state) => state.auth);
    const docScope = currentUser?.roles?.includes('ROLE_ADMIN') ? 'system' : undefined;
    const [settings, setSettings] = useState({
        pattern: '',
        number_cycle: 'yearly',
        start_number: 1,
    });
    const [preview, setPreview] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [hoveredButton, setHoveredButton] = useState(null);
    const [focusedInput, setFocusedInput] = useState(null);

    // NEUE STATE-VARIABLEN für die Belegarten
    const [documentTypes, setDocumentTypes] = useState([]);
    const [selectedDocTypeId, setSelectedDocTypeId] = useState('');



    const placeholders = [
        { placeholder: '{Kundennummer}', description: 'Die ID des Rechnungsempfängers.' },
        { placeholder: '{Datum:%Y}', description: 'Das aktuelle Jahr, vierstellig (z.B. 2025).' },
        { placeholder: '{Datum:%m}', description: 'Der aktuelle Monat, zweistellig (z.B. 07).' },
        { placeholder: '{Datum:%d}', description: 'Der aktuelle Tag, zweistellig (z.B. 31).' },
        { placeholder: '{Datum:%Y-%m-%d}', description: 'Das volle Datum (z.B. 2025-07-31).' },
        { placeholder: '{Datum:%d.%m.%Y}', description: 'Das volle Datum im deutschen Format (z.B. 31.07.2025).' },
        { placeholder: '{Datum:%Y%m%d}', description: 'Aktuelles Datum (JJJJMMTT).' },
    ];



// NEU: Lädt alle verfügbaren Belegarten beim Start
useEffect(() => {
    setLoading(true);
    UserService.getDocumentTypes(docScope)
        .then(response => {
            setDocumentTypes(response.data);
            setLoading(false);
        })
        .catch(err => {
            setError('Fehler beim Laden der verfügbaren Belegarten.');
            setLoading(false);
        });
}, [docScope]);

// NEU: Lädt die spezifischen Einstellungen, wenn eine Belegart ausgewählt wird
const fetchSettingsForDocType = useCallback((docTypeId) => {
    if (!docTypeId) {
        setSettings({ pattern: '', number_cycle: 'yearly', start_number: 1 });
        setPreview('');
        return;
    }
    setLoading(true);
    setError('');
    setSuccess('');
    UserService.getInvoiceNumberSettings(docTypeId)
        .then(response => {
            setSettings(response.data);
            setLoading(false);
        })
        .catch(err => {
            setError('Fehler beim Laden der Einstellungen für diese Belegart.');
            setLoading(false);
        });
}, []);

    const getCyclePlaceholder = useCallback(() => {
        switch (settings.number_cycle) {
            case 'daily': return '{FORTLAUFENDE_NUMMER_AUF_TAGES_BASIS}';
            case 'monthly': return '{FORTLAUFENDE_NUMMER_AUF_MONATS_BASIS}';
            case 'yearly': return '{FORTLAUFENDE_NUMMER_AUF_JAHRES_BASIS}';
            case 'continuous': return '{FORTLAUFENDE_NUMMER_AB_WERT}';
            default: return '';
        }
    }, [settings.number_cycle]);

    const generatePreview = useCallback((currentSettings) => {
        let previewPattern = currentSettings.pattern || '';
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        previewPattern = previewPattern.replace(/{Datum:([^}]+)}/g, (match, format) => {
            return format.replace(/%Y/g, year).replace(/%m/g, month).replace(/%d/g, day);
        });
        previewPattern = previewPattern.replace(/{Kundennummer}/g, '123');
        previewPattern = previewPattern.replace(getCyclePlaceholder(), String(currentSettings.start_number || 1));
        setPreview(previewPattern);
    }, [getCyclePlaceholder]);   

    useEffect(() => {
        if (settings.pattern) {
            generatePreview(settings);
        }
    }, [settings, generatePreview]);

    // NEU: Handler für das Dropdown-Menü
    const handleDocTypeChange = (e) => {
        const newDocTypeId = e.target.value;
        setSelectedDocTypeId(newDocTypeId);
        fetchSettingsForDocType(newDocTypeId);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setSettings(prev => ({ ...prev, [name]: value }));
    };

    const handlePlaceholderClick = (placeholder) => {
        setSettings(prev => ({ ...prev, pattern: (prev.pattern || '') + placeholder }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        if (!settings.pattern.includes(getCyclePlaceholder())) {
            setError(`Fehler: Das Muster muss den Platzhalter ${getCyclePlaceholder()} enthalten, der zu Ihrem gewählten Nummernkreis passt.`);
            setLoading(false);
            return;
        }

        // NEU: Die doc_type_id wird an das Backend mitgesendet
        const payload = { ...settings, doc_type_id: selectedDocTypeId };

        UserService.updateInvoiceNumberSettings(payload)
            .then((response) => {
                setSuccess(response.data.message || 'Einstellungen erfolgreich gespeichert!');
                setLoading(false);
            })
            .catch(err => {
                const errMsg = (err.response?.data?.message) || err.message || 'Unbekannter Fehler.';
                setError('Fehler beim Speichern der Einstellungen: ' + errMsg);
                setLoading(false);
            });
    };

    const styles = {
        container: {
            maxWidth: '800px',
            margin: '0 auto',
            padding: '20px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            minHeight: '100vh',
            background: 'linear-gradient(to bottom, #f5f5f7 0%, #ffffff 100%)',
        },
        card: {
            backgroundColor: '#ffffff',
            borderRadius: '20px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            overflow: 'hidden',
            transition: 'transform 0.3s ease',
        },
        cardHeader: {
            background: 'linear-gradient(135deg, #5e5ce6 0%, #7c3aed 50%, #ec4899 100%)',
            color: '#ffffff',
            padding: '30px',
            fontSize: '28px',
            fontWeight: '700',
            textAlign: 'center',
            letterSpacing: '-0.5px',
        },
        cardBody: {
            padding: '40px',
            backgroundColor: '#fafafa',
        },
        formGroup: {
            marginBottom: '30px',
        },
        // Hinweis: Das responsive Layout (ab 768px nebeneinander) wird ueber die
        // CSS-Klasse ".invoice-form-row" geregelt (siehe injizierter <style>-Block).
        // Inline-Styles in React unterstuetzen keine @media-Queries.
        label: {
            fontWeight: '600',
            color: '#1d1d1f',
            fontSize: '16px',
            marginBottom: '8px',
            display: 'block',
            flex: '0 0 auto',
            minWidth: '200px',
        },
        inputWrapper: {
            flex: '1',
            position: 'relative',
        },
        input: {
            width: '100%',
            padding: '14px 18px',
            fontSize: '16px',
            borderRadius: '12px',
            border: '2px solid transparent',
            backgroundColor: '#ffffff',
            transition: 'all 0.3s ease',
            fontFamily: 'inherit',
            outline: 'none',
            boxSizing: 'border-box',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
        },
        inputFocused: {
            borderColor: '#5e5ce6',
            boxShadow: '0 0 0 4px rgba(94, 92, 230, 0.1), 0 2px 8px rgba(0, 0, 0, 0.04)',
            transform: 'translateY(-1px)',
        },
        inputDisabled: {
            backgroundColor: '#f5f5f7',
            color: '#86868b',
            cursor: 'not-allowed',
            boxShadow: 'none',
        },
        select: {
            width: '100%',
            padding: '14px 45px 14px 18px',
            fontSize: '16px',
            borderRadius: '12px',
            border: '2px solid transparent',
            backgroundColor: '#ffffff',
            transition: 'all 0.3s ease',
            fontFamily: 'inherit',
            outline: 'none',
            cursor: 'pointer',
            appearance: 'none',
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'14\' height=\'8\' viewBox=\'0 0 14 8\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M1 1l6 6 6-6\' stroke=\'%235e5ce6\' stroke-width=\'2\' fill=\'none\' fill-rule=\'evenodd\'/%3E%3C/svg%3E")',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 18px center',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
        },
        buttonGroup: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
        },
        placeholderButton: {
            padding: '10px 16px',
            borderRadius: '10px',
            border: 'none',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            fontFamily: 'inherit',
            position: 'relative',
            overflow: 'hidden',
        },
        placeholderButtonSecondary: {
            backgroundColor: '#f5f5f7',
            color: '#1d1d1f',
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.06)',
        },
        placeholderButtonSecondaryHover: {
            backgroundColor: '#e8e8ed',
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        },
        placeholderButtonPrimary: {
            background: 'linear-gradient(135deg, #ff6b6b 0%, #ff8e53 100%)',
            color: '#ffffff',
            boxShadow: '0 2px 6px rgba(255, 107, 107, 0.2)',
        },
        placeholderButtonPrimaryHover: {
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 20px rgba(255, 107, 107, 0.3)',
        },

        // Neue farbenfrohe Button-Stile
        buttonColor1: { background: 'linear-gradient(135deg, #2E7D32 0%, #4CAF50 100%)', color: '#ffffff', boxShadow: '0 2px 6px rgba(46, 125, 50, 0.2)' },
        buttonColor1Hover: { boxShadow: '0 6px 20px rgba(46, 125, 50, 0.3)', transform: 'translateY(-2px)' },
        
        buttonColor2: { background: 'linear-gradient(135deg, #1565C0 0%, #2196F3 100%)', color: '#ffffff', boxShadow: '0 2px 6px rgba(21, 101, 192, 0.2)' },
        buttonColor2Hover: { boxShadow: '0 6px 20px rgba(21, 101, 192, 0.3)', transform: 'translateY(-2px)' },

        buttonColor3: { background: 'linear-gradient(135deg, #FF6F00 0%, #FFC107 100%)', color: '#ffffff', boxShadow: '0 2px 6px rgba(255, 111, 0, 0.2)' },
        buttonColor3Hover: { boxShadow: '0 6px 20px rgba(255, 111, 0, 0.3)', transform: 'translateY(-2px)' },

        buttonColor4: { background: 'linear-gradient(135deg, #6A1B9A 0%, #9C27B0 100%)', color: '#ffffff', boxShadow: '0 2px 6px rgba(106, 27, 154, 0.2)' },
        buttonColor4Hover: { boxShadow: '0 6px 20px rgba(106, 27, 154, 0.3)', transform: 'translateY(-2px)' },

        // Spezielle Farbe für den Zähler-Button
        buttonColorSpecial: { background: 'linear-gradient(135deg, #C62828 0%, #F44336 100%)', color: '#ffffff', boxShadow: '0 2px 6px rgba(198, 40, 40, 0.2)' },
        buttonColorSpecialHover: { boxShadow: '0 6px 20px rgba(198, 40, 40, 0.3)', transform: 'translateY(-2px)' },




        submitButton: {
            padding: '16px 40px',
            borderRadius: '12px',
            border: 'none',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            background: 'linear-gradient(135deg, #5e5ce6 0%, #7c3aed 100%)',
            color: '#ffffff',
            display: 'block',
            margin: '40px auto 0',
            fontFamily: 'inherit',
            minWidth: '200px',
            boxShadow: '0 4px 14px rgba(94, 92, 230, 0.3)',
        },
        submitButtonHover: {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 24px rgba(94, 92, 230, 0.4)',
        },
        submitButtonDisabled: {
            background: '#d2d2d7',
            cursor: 'not-allowed',
            transform: 'none',
            boxShadow: 'none',
        },
        alert: {
            padding: '16px 20px',
            borderRadius: '12px',
            marginBottom: '24px',
            fontWeight: '500',
            fontSize: '15px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
        },
        alertDanger: {
            backgroundColor: '#fee4e2',
            color: '#d13b2e',
            border: '1px solid #fecaca',
        },
        alertSuccess: {
            backgroundColor: '#d1f4e0',
            color: '#117848',
            border: '1px solid #a7f3d0',
        },
        helpText: {
            fontSize: '14px',
            color: '#86868b',
            marginTop: '8px',
            lineHeight: '1.6',
        },
        spinner: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
        },
        spinnerIcon: {
            width: '48px',
            height: '48px',
            border: '4px solid #e5e5e7',
            borderTopColor: '#5e5ce6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
        },
        previewInput: {
            backgroundColor: '#f0f0f5',
            fontWeight: '500',
            color: '#5e5ce6',
        },
    };

    // Responsive Regeln, die sich nicht als Inline-Style abbilden lassen
    // (React unterstuetzt keine @media-Queries in style={}). Wird einmalig
    // ueber einen <style>-Block in den DOM injiziert.
    const responsiveCss = `
        .invoice-form-row {
            display: flex;
            flex-direction: column;
            gap: 12px;
        }
        @media (min-width: 768px) {
            .invoice-form-row {
                flex-direction: row;
                align-items: flex-start;
            }
        }
    `;


    // Die Ladeanzeige wird nur noch am Anfang gezeigt, wenn die Belegarten geladen werden
    if (loading && !documentTypes.length) {
        return <div style={styles.container}><div style={styles.spinner}><Spinner animation="border" style={{color: '#5e5ce6'}}/></div></div>;
    }

    const buttonColorStyles = [styles.buttonColor1, styles.buttonColor2, styles.buttonColor3, styles.buttonColor4];
    const buttonColorHoverStyles = [styles.buttonColor1Hover, styles.buttonColor2Hover, styles.buttonColor3Hover, styles.buttonColor4Hover];


    return (
        <div style={styles.container}>
            <style>{responsiveCss}</style>
            <div style={styles.card}>
                <div style={styles.cardHeader}>Einstellungen für Belegnummern</div>
                <div style={styles.cardBody}>
                    {error && <div style={{...styles.alert, ...styles.alertDanger}}><span>⚠️</span> {error}</div>}
                    {success && <div style={{...styles.alert, ...styles.alertSuccess}}><span>✅</span> {success}</div>}

                    <form onSubmit={handleSubmit}>
                        {/* +++ ANFANG: NEUES DROPDOWN FÜR BELEGARTEN +++ */}
                        <div style={styles.formGroup}>
                            <div className="invoice-form-row">
                                <label style={styles.label}>Belegart auswählen</label>
                                <div style={styles.inputWrapper}>
                                    <select
                                        name="doc_type_id"
                                        value={selectedDocTypeId}
                                        onChange={handleDocTypeChange}
                                        style={styles.select}
                                        required
                                    >
                                        <option value="">Bitte eine Belegart auswählen...</option>
                                        {documentTypes.map(doc => (
                                            <option key={doc.doc_type_id} value={doc.doc_type_id}>
                                                {doc.name}
                                            </option>
                                        ))}
                                    </select>
                                    <div style={styles.helpText}>
                                        Wählen Sie die Belegart, für die Sie das Nummernformat festlegen möchten.
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* +++ ENDE: NEUES DROPDOWN FÜR BELEGARTEN +++ */}

                        {/* Der Rest des Formulars wird nur angezeigt, wenn eine Belegart ausgewählt ist */}
                        {selectedDocTypeId && (
                            <fieldset disabled={loading}>
                                <div style={styles.formGroup}>
                                    <div className="invoice-form-row">
                                        <label style={styles.label}>Format-Definitionsfeld</label>
                                        <div style={styles.inputWrapper}>
                                            <input type="text" name="pattern" value={settings.pattern || ''} onChange={handleChange} onFocus={() => setFocusedInput('pattern')} onBlur={() => setFocusedInput(null)} placeholder="z.B. RE-{Datum:%Y}-..." required style={{...styles.input, ...(focusedInput === 'pattern' ? styles.inputFocused : {})}}/>
                                        </div>
                                    </div>
                                </div>

                                <div style={styles.formGroup}>
                                    <div className="invoice-form-row">
                                        <label style={styles.label}>Platzhalter einfügen</label>
                                        <div style={styles.buttonGroup}>
                                            {placeholders.map((p, index) => (
                                                <button key={p.placeholder} type="button" onClick={() => handlePlaceholderClick(p.placeholder)} onMouseEnter={() => setHoveredButton(p.placeholder)} onMouseLeave={() => setHoveredButton(null)} title={p.description} style={{...styles.placeholderButton, ...buttonColorStyles[index % buttonColorStyles.length], ...(hoveredButton === p.placeholder ? buttonColorHoverStyles[index % buttonColorHoverStyles.length] : {})}}>
                                                    {p.placeholder}
                                                </button>
                                            ))}
                                            <button type="button" onClick={() => handlePlaceholderClick(getCyclePlaceholder())} onMouseEnter={() => setHoveredButton('cycle')} onMouseLeave={() => setHoveredButton(null)} title="Der fortlaufende Zähler." style={{...styles.placeholderButton, ...styles.buttonColorSpecial, ...(hoveredButton === 'cycle' ? styles.buttonColorSpecialHover : {})}}>
                                                {getCyclePlaceholder()}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div style={styles.formGroup}>
                                    <div className="invoice-form-row">
                                        <label style={styles.label}>Nummernkreis</label>
                                        <div style={styles.inputWrapper}>
                                            <select name="number_cycle" value={settings.number_cycle || 'yearly'} onChange={handleChange} onFocus={() => setFocusedInput('number_cycle')} onBlur={() => setFocusedInput(null)} style={{...styles.select, ...(focusedInput === 'number_cycle' ? styles.inputFocused : {})}}>
                                                <option value="daily">Täglich zurücksetzen</option>
                                                <option value="monthly">Monatlich zurücksetzen</option>
                                                <option value="yearly">Jährlich zurücksetzen</option>
                                                <option value="continuous">Fortlaufend (nie zurücksetzen)</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div style={styles.formGroup}>
                                    <div className="invoice-form-row">
                                        <label style={styles.label}>Startnummer</label>
                                        <div style={styles.inputWrapper}>
                                            <input type="number" name="start_number" value={settings.start_number || 1} onChange={handleChange} onFocus={() => setFocusedInput('start_number')} onBlur={() => setFocusedInput(null)} min="1" style={{...styles.input, ...(focusedInput === 'start_number' ? styles.inputFocused : {})}}/>
                                        </div>
                                    </div>
                                </div>

                                <div style={styles.formGroup}>
                                    <div className="invoice-form-row">
                                        <label style={styles.label}>Vorschau</label>
                                        <div style={styles.inputWrapper}>
                                            <input type="text" value={preview} readOnly disabled style={{...styles.input, ...styles.inputDisabled, ...styles.previewInput}}/>
                                        </div>
                                    </div>
                                </div>

                                <button type="submit" disabled={loading} onMouseEnter={() => setHoveredButton('submit')} onMouseLeave={() => setHoveredButton(null)} style={{...styles.submitButton, ...(hoveredButton === 'submit' && !loading ? styles.submitButtonHover : {}), ...(loading ? styles.submitButtonDisabled : {})}}>
                                    {loading ? 'Speichern...' : 'Einstellungen speichern'}
                                </button>
                            </fieldset>
                        )}
                    </form>
                </div>
            </div>
        </div>
    );
};

export default BoardInvoiceSettings;